import { useFormik } from 'formik';
import { Form } from 'react-bootstrap';
import * as Yup from 'yup';
import { Modal, TextInput } from '@pmi/dsm-react';
import {
  AgreementApiModel,
  PrimaryContactApiModel,
} from '../../../../shared/models/DealModel';
import { normalizeEmail, singleEmailRegex } from '../../../../shared/helpers';
import { toastWithAnalytics } from '../../../../shared/analytics';
import { useUpdatePointOfContactMutation } from './apiSlice';

interface EditPointOfContactModalProps {
  accountId: string;
  deal: AgreementApiModel;
  onCancel?: () => void;
}

export default function EditPointOfContactModal({
  accountId,
  deal,
  onCancel = () => {},
}: EditPointOfContactModalProps) {
  const [updateDealStatus] = useUpdatePointOfContactMutation();

  const initialData: PrimaryContactApiModel = {
    firstName: deal.primaryContact?.firstName,
    lastName: deal.primaryContact?.lastName,
    email: deal.primaryContact?.email,
    phoneNumber: deal.primaryContact?.phoneNumber,
  };
  const validationSchema = Yup.object<PrimaryContactApiModel>().shape({
    firstName: Yup.string().required('First Name is required'),
    lastName: Yup.string().required('Last Name is required'),
    email: Yup.string()
      .required('Email is required')
      // validate the normalized value so a pasted " a@b.com " is accepted,
      // while Formik state keeps the raw user input (AD-04)
      .test('single-email', 'Please enter a valid email', (value) =>
        singleEmailRegex.test(normalizeEmail(value)),
      ),
    phoneNumber: Yup.string().optional(),
  });

  const onSave = async (model: PrimaryContactApiModel) => {
    updateDealStatus({
      accountId,
      dealId: deal.id,
      payload: { ...model, email: normalizeEmail(model.email) },
    })
      .unwrap()
      .then(() => {
        toastWithAnalytics(`Point of contact updated successfully`, 'success');
      })
      .catch(() => {
        toastWithAnalytics('Error while updating point of contact', 'danger');
      });
  };

  const onCancelHandler = () => {
    onCancel();
  };

  const formik = useFormik({
    initialValues: initialData,
    validateOnChange: false,
    validateOnBlur: false,
    onSubmit: (values: PrimaryContactApiModel) => {
      onSave(values);
    },
    validationSchema,
  });

  const onUpdateClick = () => {
    formik.submitForm();
  };

  return (
    <Modal
      show
      onClose={onCancelHandler}
      headline="Edit Point of Contact"
      description="The point of contact is responsible for managing the relationship and ensuring smooth communication between PMI and our customer. Most often this will be the customer's Partner Success Manager (PSM)."
      buttons={[
        {
          titleText: 'Cancel',
          variant: 'outline-secondary',
          onClick: onCancelHandler,
        },
        {
          titleText: 'Save Changes',
          // disable button when there are no changes in the form
          disabled: !formik.dirty,
          variant: 'primary',
          onClick: onUpdateClick,
        },
      ]}
    >
      <Form>
        <div className="row gy-3 row-cols-1 mb-2">
          <div className="col" data-testid="first_name_container_testid">
            <Form.Group
              controlId="firstName"
              className={`${formik.errors.firstName ? 'is-invalid' : ''}`}
            >
              <Form.Label>First Name</Form.Label>
              <TextInput
                name="firstName"
                value={formik.values.firstName}
                className={`${formik.errors.firstName ? 'is-invalid' : ''}`}
                onChange={formik.handleChange}
                isInvalid={!!formik.errors.firstName}
                validationMessage={formik.errors.firstName}
              />
            </Form.Group>
          </div>
          <div className="col">
            <Form.Group
              controlId="lastName"
              className={`${formik.errors.lastName ? 'is-invalid' : ''}`}
            >
              <Form.Label>Last Name</Form.Label>
              <TextInput
                name="lastName"
                value={formik.values.lastName}
                className={`${formik.errors.lastName ? 'is-invalid' : ''}`}
                onChange={formik.handleChange}
                isInvalid={!!formik.errors.lastName}
                validationMessage={formik.errors.lastName}
              />
            </Form.Group>
          </div>
          <div className="col">
            <Form.Group
              controlId="email"
              className={`${formik.errors.email ? 'is-invalid' : ''}`}
            >
              <Form.Label>Email</Form.Label>
              <TextInput
                name="email"
                value={formik.values.email}
                className={`${formik.errors.email ? 'is-invalid' : ''}`}
                onChange={formik.handleChange}
                isInvalid={!!formik.errors.email}
                validationMessage={formik.errors.email}
              />
            </Form.Group>
          </div>
          <div className="col">
            <Form.Group
              controlId="phoneNumber"
              className={`${formik.errors.phoneNumber ? 'is-invalid' : ''}`}
            >
              <Form.Label>Phone Number</Form.Label>
              <TextInput
                name="phoneNumber"
                value={formik.values.phoneNumber}
                className={`${formik.errors.phoneNumber ? 'is-invalid' : ''}`}
                onChange={formik.handleChange}
                isInvalid={!!formik.errors.phoneNumber}
                validationMessage={formik.errors.phoneNumber}
              />
            </Form.Group>
          </div>
        </div>
      </Form>
    </Modal>
  );
}
